import { useMemo, useState } from "react";
import { useOrganizationsData } from "@src/presentation/providers/OrganizationsDataProvider";
import ErrorBoundary from "@src/presentation/components/common/ErrorBoundary";
import {
  Button,
  Flex,
  Form,
  Heading,
  Modal,
  Paragraph,
  useToast,
} from "@ir/ui";
import { PageHeader } from "@src/presentation/components/Layout/common";
import {
  OrganizationsAddEditForm,
  type OrganizationFormValues,
} from "./OrganizationsAddEditForm";
import { useUpdateOrganization } from "./useUpdateOrganization";
import { normalizeOrganizationLogoFile } from "@src/domain/dto/organizations";
import type { Organization } from "@src/domain/entities/organization";
import {
  isOrganizationKeyDuplicateError,
  ORGANIZATION_KEY_ALREADY_EXISTS_MESSAGE,
} from "./organizationKeyValidation";
import HasAnyPermission from "@src/presentation/components/permissions/HasAnyPermission";
import { Permissions } from "@src/domain/permissions";

interface OrganizationsHeaderProps {
  loading: boolean;
  activeOrganizationCount?: number;
  activeBrandCount?: number;
  error?: string | null;
  onOrganizationSaved?: (organization: Organization) => Promise<void> | void;
}

export const OrganizationsHeader = ({
  loading,
  activeOrganizationCount,
  activeBrandCount,
  error,
  onOrganizationSaved,
}: OrganizationsHeaderProps) => {
  const organizationLabel =
    activeOrganizationCount === 1
      ? "Active organization"
      : "Active organizations";
  const brandLabel = activeBrandCount === 1 ? "Active brand" : "Active brands";
  const subtitle = loading
    ? "Loading organizations..."
    : `${activeOrganizationCount} ${organizationLabel} · ${activeBrandCount} ${brandLabel}`;

  const [isAddOrganizationModalOpen, setIsAddOrganizationModalOpen] =
    useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isFormSubmittable, setIsFormSubmittable] = useState(false);
  const [form] = Form.useForm<OrganizationFormValues>();
  const updateOrganization = useUpdateOrganization();
  const { getKnownOrganizationKeys } = useOrganizationsData();
  const { success: successToast, error: errorToast } = useToast();

  const existingOrganizationKeys = useMemo(
    () => getKnownOrganizationKeys(),
    [getKnownOrganizationKeys],
  );

  const initialValues = useMemo<OrganizationFormValues>(
    () => ({
      organizationId: null,
      organizationKey: "",
      organizationName: "",
      organizationDisplayName: "",
      organizationDescription: "",
      organizationLogoFile: null,
      removeOrganizationLogo: false,
      organizationIsActive: true,
    }),
    [],
  );

  const handleSubmit = async (values: OrganizationFormValues) => {
    setIsSaving(true);
    try {
      const logoFile = normalizeOrganizationLogoFile(
        values.organizationLogoFile,
      );
      const payload = {
        organizationId: null,
        organizationKey: values.organizationKey?.trim() ?? "",
        organizationName: values.organizationName?.trim() ?? "",
        organizationDisplayName: values.organizationDisplayName?.trim() ?? "",
        organizationDescription: values.organizationDescription?.trim() || null,
        organizationIsActive: Boolean(values.organizationIsActive),
        ...(logoFile ? { organizationLogoFile: logoFile } : {}),
      };

      const saved = await updateOrganization(payload);
      await onOrganizationSaved?.(saved);
      successToast({ content: "Organization saved successfully." });
      setIsAddOrganizationModalOpen(false);
      form.resetFields();
      setIsFormSubmittable(false);
    } catch (saveError: unknown) {
      console.error("Failed to save organization", saveError);
      if (isOrganizationKeyDuplicateError(saveError)) {
        form.setFields([
          {
            name: "organizationKey",
            errors: [ORGANIZATION_KEY_ALREADY_EXISTS_MESSAGE],
          },
        ]);
        setIsFormSubmittable(false);
      }
      errorToast({
        content: "Failed to save organization. Please try again.",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <ErrorBoundary>
      <PageHeader>
        <Flex align="center" gap={1} justifyContent="space-between">
          <Flex direction="column">
            <Heading level={5} className="text-grey-700 font-bold m-0">
              Organizations & Brands
            </Heading>
            <Paragraph className="text-grey-500 font-regular m-0">
              {subtitle}
            </Paragraph>
            {error && (
              <Paragraph className="text-danger font-regular m-0">
                {error}
              </Paragraph>
            )}
          </Flex>
          <Flex>
            <HasAnyPermission requiredPermissions={[Permissions.ORGANIZATIONS_ADD]}>
              <Button
                size="small"
                onPress={() => setIsAddOrganizationModalOpen(true)}
              >
                Add Organization
              </Button>
            </HasAnyPermission>
          </Flex>
        </Flex>
      </PageHeader>
      <Modal
        isOpen={isAddOrganizationModalOpen}
        onClose={() => setIsAddOrganizationModalOpen(false)}
        headerContent={
          <Heading level={6} className="text-grey-700 font-bold m-0">
            Add Organization
          </Heading>
        }
        footerContent={
          <Flex gap={0.5}>
            <Button
              onPress={() => setIsAddOrganizationModalOpen(false)}
              color="grey"
              isDisabled={isSaving}
              size="small"
            >
              Cancel
            </Button>
            <Button
              color="primary"
              onPress={() => form.submit()}
              isLoading={isSaving}
              isDisabled={isSaving || !isFormSubmittable}
              size="small"
            >
              Save
            </Button>
          </Flex>
        }
        size="large"
      >
        <OrganizationsAddEditForm
          form={form}
          mode="create"
          initialValues={initialValues}
          existingOrganizationKeys={existingOrganizationKeys}
          onSubmit={handleSubmit}
          onFormValidityChange={setIsFormSubmittable}
        />
      </Modal>
    </ErrorBoundary>
  );
};
